/**
 * Shared axios interceptors for EMALINO API clients.
 *
 * UI and SI both create their own axios instance with their own auth
 * refresh / error notification / device attestation logic. The handful
 * of primitives that genuinely should be identical across both apps
 * (correlation-ID injection, bearer-token attachment, 503 logging)
 * live here as small installer functions so each app's `boot/api.ts`
 * stays the source of truth for its own behavior.
 *
 * Anti-pattern to avoid: inverting control with a `createApiClient`
 * factory. UI's request flow has ~480 lines of UI-specific concerns
 * (refresh queue, recaptcha, file-upload axios, etc.) that don't
 * belong in shared code.
 */

import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/** Header used for end-to-end request tracing across UI/SI/API. */
export const CORRELATION_ID_HEADER = 'X-EMALINO-Correlation-Id';

/**
 * Property name where the generated correlation ID is stashed on the
 * axios request config object. UI's response interceptor reads this to
 * derive a `REF-XXXXXXXX` support code on errors.
 */
export const CORRELATION_ID_CONFIG_KEY = '_correlationId';

/**
 * Property name set on an axios request config to opt that request's 404
 * responses OUT of the global error toast. Resource-detail fetches
 * (useResourceCore.fetchSingle) set this so a missing resource renders a
 * single in-route "not found" state instead of a red toast — a 404 from a
 * stale or mistyped link is an expected, user-recoverable condition, not a
 * system fault.
 */
export const SUPPRESS_NOTFOUND_TOAST_CONFIG_KEY = '_suppressNotFoundToast';

/**
 * Per-request opt-out for the interceptor's generic 409-conflict toast. Set
 * on a request whose caller owns a specific conflict message (e.g. a
 * duplicate-link/relationship composable), so the user sees one precise toast
 * instead of the generic server message plus the specific one.
 */
export const SUPPRESS_CONFLICT_TOAST_CONFIG_KEY = '_suppressConflictToast';

/**
 * Per-request opt-out of the app's generic error toast. Set on probe-style
 * requests whose caller fully owns failure handling — e.g. the support-chat
 * session-restore validation, where a 403/404/410 for a stale stored session
 * is an expected, self-healing outcome (the store clears it and starts
 * fresh), not a fault to surface. Only generic message toasts are
 * suppressed: structural handlers (401 refresh/logout, consent gate,
 * maintenance mode, staff permission resync) still run. Enforcement point
 * per app: UI guards centrally at the top of handleApiError (all statuses);
 * SI guards its 403 toast — currently its only generic display. Any NEW
 * generic toast site in an interceptor must repeat this guard.
 */
export const SUPPRESS_ERROR_TOAST_CONFIG_KEY = '_suppressErrorToast';

/**
 * Generate a UUID per request, set it as the correlation header, and
 * stash it on the request config for the response handler to read.
 */
export function installCorrelationIdInterceptor(axios: AxiosInstance): void {
  axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const correlationId = crypto.randomUUID();
    config.headers.set(CORRELATION_ID_HEADER, correlationId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (config as any)[CORRELATION_ID_CONFIG_KEY] = correlationId;
    return config;
  });
}

/** Client identity headers — semantic version of the running bundle. */
export const CLIENT_VERSION_HEADER = 'X-EMALINO-Client-Version';
/** Client identity headers — monotonic build number (git commit count). */
export const CLIENT_BUILD_HEADER = 'X-EMALINO-Client-Build';
/** Client identity headers — short git hash (may carry a -dirty suffix). */
export const CLIENT_GIT_HASH_HEADER = 'X-EMALINO-Client-Git-Hash';
/** Client identity headers — IANA timezone of the user agent. */
export const CLIENT_TIMEZONE_HEADER = 'X-EMALINO-Client-Timezone';

/**
 * Stamp the bundle's identity (version / build / commit, from the
 * VITE_APP_* constants quasar.config.ts injects at build time) plus the
 * user agent's timezone onto every request of the given instance. The
 * API persists these on auth events and device sessions
 * (AuthenticationAuditMiddleware / SessionManager → audit_auths and
 * auth_device_sessions client_* columns).
 *
 * The values are constant for the lifetime of the bundle, so they are
 * set as instance defaults rather than via a per-request interceptor.
 * Every header listed here MUST stay in api/config/cors.php
 * allowed_headers — an unlisted custom header fails the browser's CORS
 * preflight and silently kills the request (see apps/si/src/boot/api.ts).
 */
export function installClientIdentityHeaders(axios: AxiosInstance): void {
  const headers = axios.defaults.headers.common;
  headers[CLIENT_VERSION_HEADER] = (import.meta.env.VITE_APP_VERSION as string) || '0.0.0';
  headers[CLIENT_BUILD_HEADER] = (import.meta.env.VITE_APP_BUILD as string) || '0';

  const gitHash = import.meta.env.VITE_APP_GIT_HASH as string | undefined;
  if (gitHash) {
    headers[CLIENT_GIT_HASH_HEADER] = gitHash;
  }

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      headers[CLIENT_TIMEZONE_HEADER] = timezone;
    }
  } catch {
    // Intl unavailable in this runtime — identity headers still apply.
  }
}

/**
 * Attach `Authorization: Bearer ${token}` from the supplied getter on
 * every outgoing request. The getter is called per-request so it picks
 * up token rotations from refresh flows.
 */
export function installBearerTokenInterceptor(
  axios: AxiosInstance,
  getAccessToken: () => string | null | undefined,
): void {
  axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  });
}

/**
 * Log 503 responses through the supplied warn function. Per-app code
 * is still responsible for surfacing maintenance mode to the user
 * (banner, redirect, etc.) — this just centralizes the log line.
 */
export function installMaintenanceModeLogger(
  axios: AxiosInstance,
  warn: (message: string) => void,
): void {
  axios.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      const axiosError = error as { response?: { status: number } };
      if (axiosError.response?.status === 503) {
        warn('API is in maintenance mode');
      }
      // Re-reject the ORIGINAL error untouched. Wrapping a non-Error rejection
      // in `new Error(String(error))` stripped `.response`/`.config`, which the
      // downstream app interceptors (api.ts 401/maintenance handling) read — so
      // a non-AxiosError rejection would silently lose its shape here.
      return Promise.reject(error);
    },
  );
}

/**
 * Read back the correlation ID stashed on a request config by
 * `installCorrelationIdInterceptor`. Used by response handlers to
 * generate user-visible support reference codes.
 */
export function getCorrelationIdFromConfig(
  config: InternalAxiosRequestConfig | undefined,
): string | undefined {
  if (!config) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (config as any)[CORRELATION_ID_CONFIG_KEY] as string | undefined;
}

/**
 * Derive a short user-visible reference code from a correlation ID.
 * Format: `REF-{first 8 hex chars}` — matches the backend format
 * emitted by `CorrelationContext::getShortRef()`.
 */
export function deriveErrorRef(correlationId?: string): string | undefined {
  if (!correlationId) return undefined;
  return 'REF-' + correlationId.replace(/-/g, '').substring(0, 8).toUpperCase();
}
